import { Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IBreadcrumbsAndMenu, IGlobalSetting } from "../../../types/global";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import GlobalSettingTable from "../../../components/Table/Setting/Global";
import { useModal } from "../../../hooks/use-modal";
import GlobalSettingModal from "../../../components/UI/Modal/GlobalSettingModal";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { GraphQLClient } from "graphql-request";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";
import {
  GeneralModelType,
  UnitFindUniqueQuery,
  UnitsFindAllQuery,
  useUnitCreateMutation,
  useUnitDeleteMutation,
  useUnitFindUniqueQuery,
  useUnitUpdateMutation,
  useUnitsFindAllQuery,
} from "../../../generated/general";
import ConfirmationModal from "../../../components/UI/Modal/ConfirmationModal";

const UomSetting = () => {
  const { t } = useTranslation();
  const [deletingId, setDeletingId] = useState<number>();

  let [searchParams, setSearchParams] = useSearchParams();

  const id = searchParams.get("id");

  const setSearchParamsHandler = (key: string, value: string) => {
    searchParams.set(key, value);
    setSearchParams(searchParams);
  };

  const removeSearchParamsHandler = (key: string) => {
    searchParams.delete(key);
    setSearchParams(searchParams);
  };

  const { modal, openModalHandler, closeModalHandler } = useModal(
    undefined,
    () => removeSearchParamsHandler("id")
  );

  const { enqueueSnackbar } = useSnackbar();

  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("setting.index"),
      to: "/setting",
    },
    {
      name: t("setting.inventory.index"),
      to: "/setting/inventory",
    },
    {
      name: t("setting.inventory.uom"),
    },
  ];

  const graphQLClientGeneral: GraphQLClient =
    createGraphQLClientWithMiddleware("general");

  const { data: rowData, refetch: refetchRowData } =
    useUnitsFindAllQuery<UnitsFindAllQuery>(graphQLClientGeneral);

  const { data, isLoading, isSuccess } =
    useUnitFindUniqueQuery<UnitFindUniqueQuery>(
      graphQLClientGeneral,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: Boolean(id),
      }
    );

  const { mutateAsync: createUnit } =
    useUnitCreateMutation<Error>(graphQLClientGeneral);

  const { mutateAsync: updateUnit } =
    useUnitUpdateMutation<Error>(graphQLClientGeneral);

  const { mutateAsync: deleteUnit } =
    useUnitDeleteMutation<Error>(graphQLClientGeneral);

  const onAddHandler = async (data: IGlobalSetting) => {
    try {
      await createUnit({
        data,
      });
      enqueueSnackbar(`เพิ่ม${t("setting.inventory.uom")}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`เพิ่ม${t("setting.inventory.uom")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const onEditHandler = (id?: number) => {
    if (id) {
      setSearchParamsHandler("id", id.toString());
      openModalHandler();
    }
  };

  const onUpdateHandler = async (data: IGlobalSetting) => {
    try {
      const { id, ...otherData } = data;
      await updateUnit({
        uniqueInput: {
          id: id,
        },
        data: otherData,
      });
      enqueueSnackbar(`แก้ไข${t("setting.inventory.uom")}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`แก้ไข${t("setting.inventory.uom")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const onDeleteHandler = (id?: number) => {
    if (id) {
      setDeletingId(id);
      openConfirmationHandler();
    }
  };

  const deleteActionHandler = async () => {
    try {
      await deleteUnit({
        uniqueInput: {
          id: deletingId,
        },
      });
      setDeletingId(undefined);
      enqueueSnackbar(`ลบ${t("setting.inventory.uom")}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`ลบ${t("setting.inventory.uom")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const {
    modal: confirmation,
    openModalHandler: openConfirmationHandler,
    closeModalHandler: closeConfirmationHandler,
    submitModalHandler: submitConfirmationHandler,
  } = useModal(deleteActionHandler);

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ display: "flex", justifyContent: "space-between", my: 3 }}>
        <Typography variant="h5">{t("setting.inventory.uom")}</Typography>
        <CustomizedButton
          title={t("button.add") + t("setting.inventory.uom")}
          variant="contained"
          onClick={openModalHandler}
        />
      </Box>
      <GlobalSettingTable
        data={rowData?.UnitsFindAll || []}
        onEditHandler={onEditHandler}
        onDeleteHandler={onDeleteHandler}
      />
      <GlobalSettingModal
        open={modal}
        closeModalHandler={closeModalHandler}
        onAddHandler={onAddHandler}
        onUpdateHandler={onUpdateHandler}
        data={data?.UnitFindUnique}
        isLoading={isLoading}
        isSuccess={isSuccess}
        type={GeneralModelType.Unit}
      />
      <ConfirmationModal
        title={`ยืนยันการลบ${t("setting.inventory.uom")}`}
        open={confirmation}
        handleClose={closeConfirmationHandler}
        action={submitConfirmationHandler}
      />
    </>
  );
};

export default UomSetting;
